/**
 * SMSWiser API Hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme, TrackerEnum } from '@trafficpulse/shared';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/providers/AuthProvider';
import api from '@/services/api';
import type { SmswiserDrop, SmswiserDropsAllResponse } from '@/types/smswiser-drops';

export function unwrapApiData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

// Types
export interface Country {
  id: number;
  name: string;
  key: string;
}

export interface CountryData {
  countryId: number;
  countryName: string;
  /** ISO2 land code (e.g. "AU") — scopes SMSWiser drop queries via the native filter. */
  landCode?: string;
  campaigns: SMSCampaign[];
  routes: Gateway[];
  leadsLists: LeadsList[];
  shortLinkTlds: ShortLinkTld[];
}

export interface SMSCampaign {
  id: number;
  name: string;
  /** Tracker provider for drops / traffic stats */
  trafficProvider?: TrackerEnum | null;
  /** External campaign id (Voluum UUID or Binom id) when linked */
  trafficCampaignId?: string | null;
  /** @deprecated Use trafficCampaignId; still returned by some caches */
  voluumCampaignId?: string | null;
}

export interface Gateway {
  id: number;
  name: string;
  /** Price per SMS for cost calculation (e.g. 0.025). */
  pricePerSms?: number | null;
}

export type PerformanceTag =
  | 'High FTD'
  | 'High Profit'
  | 'Low Profit'
  | 'High ROI'
  | 'High Conv';

export interface LeadsList {
  id: number;
  name: string;
  /** Voluum list id (custom-variable-4). Used to match list-performance-tags and lists-with-stats. */
  voluumId?: string | null;
  count?: number;
  /** When lead count was last fetched; null if never (badge shows "N/A"). */
  leadCountFetchedAt?: string | null;
  /** Amount used today (sent + draft). Populated when workDate is passed to useCountry. */
  usageToday?: number;
  /** Remaining = count - usageToday. Computed on backend when workDate is passed. */
  remaining?: number | null;
  /** List was first seen in catalog within last 2 days. */
  isNew?: boolean;
  /** List has never been used in any sent drop. */
  neverBeenUsed?: boolean;
  /** Performance tags (last 30 days): High FTD, High Profit, etc. */
  performanceTags?: PerformanceTag[];
}

export interface TrafficGroupTags {
  wiserIds: number[];
  trafficCampaignId: string;
  tagsByListId: Record<string, PerformanceTag[]>;
  tagsByWiserId: Record<number, PerformanceTag[]>;
}

export interface ListPerformanceTagsResponse {
  /** Tags by list id (voluumId). Use for dashboard - listRow.listId = voluumId. */
  tagsByListId: Record<string, PerformanceTag[]>;
  /** Tag + date range per list for hover tooltips. */
  tagDetailsByListId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  /** Tags by wiserId. Use for Prepare Drops / Scale modal. */
  tagsByWiserId?: Record<number, PerformanceTag[]>;
  /** Tags per traffic group (country-wide mode only). Key = comma-joined wiserIds. */
  tagsByTrafficGroup?: Record<string, TrafficGroupTags>;
  /** Maps wiserId → trafficGroup key for lookup. */
  groupByWiserId?: Record<number, string>;
  /** Custom rule tag name -> hex color for badge styling */
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
  /** Debug info for diagnosing empty tags. */
  _debug?: {
    voluumRowCount: number;
    voluumCampaignId?: string;
    trafficCampaignId?: string;
    aggregatedListIds: string[];
    leadsListCount: number;
    leadsListKeys: Array<{ wiserId: number; voluumId: string | null; name: string }>;
    listMetrics?: Record<
      string,
      Array<{
        tag: string;
        dateRangePreset: string;
        metrics: Record<string, number>;
      }>
    >;
  };
}

export interface ShortLinkTld {
  id?: number;
  value: string;
  displayName: string;
  domainCount?: number;
  tld?: string;
}

export interface CreateDropDto {
  title: string;
  country: string;
  campaign?: string;
  smsGateway: string;
  leadsList: string;
  amount: number;
  senderNames: string[];
  smsTexts: string[];
  shortLinkTld: string;
  executeAfter?: string;
  batchAmount?: number;
  batchDelay?: number;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
}

export interface Drop {
  id: string;
  uniqueId: string;
  title: string;
  status: string;
  country?: string;
  cap?: number;
  createdAt?: string;
}

// Query Keys
export const smswiserKeys = {
  all: ['smswiser'] as const,
  countries: () => [...smswiserKeys.all, 'countries'] as const,
  country: (key: string) => [...smswiserKeys.countries(), key] as const,
  drops: () => [...smswiserKeys.all, 'drops'] as const,
};

// Hooks
export function useCountries() {
  return useQuery({
    queryKey: smswiserKeys.countries(),
    queryFn: async () => {
      const response = await api.get('/smswiser/countries');
      return unwrapApiData<Country[]>(response.data);
    },
    staleTime: 3600000, // 1 hour (static data)
  });
}

/** Cache 30 min: show dashboard even if left for 20+ min; refresh in background like Refresh button. */
const COUNTRY_STALE_MS = 30 * 60 * 1000;
/** Keep in cache 1 hour: switching back or returning after 20 min shows previous data while refetching. */
const COUNTRY_GC_MS = 60 * 60 * 1000;

export function useCountry(countryKey: string, workDate?: string) {
  return useQuery({
    queryKey: workDate
      ? [...smswiserKeys.country(countryKey), 'workDate', workDate]
      : smswiserKeys.country(countryKey),
    queryFn: async () => {
      const params = workDate ? { workDate } : {};
      const response = await api.get(`/smswiser/countries/${countryKey}`, {
        params,
      });
      return unwrapApiData<CountryData>(response.data);
    },
    enabled: !!countryKey,
    staleTime: workDate ? 60000 : COUNTRY_STALE_MS,
    gcTime: COUNTRY_GC_MS,
    placeholderData: keepPreviousData,
  });
}

export function useCampaignsByCountry(countryKey: string) {
  return useQuery({
    queryKey: [...smswiserKeys.country(countryKey), 'campaigns'],
    queryFn: async () => {
      const response = await api.get(`/smswiser/countries/${countryKey}/campaigns`);
      return unwrapApiData<SMSCampaign[]>(response.data);
    },
    enabled: !!countryKey,
    staleTime: 3600000,
  });
}

export function useGatewaysByCountry(countryKey: string) {
  return useQuery({
    queryKey: [...smswiserKeys.country(countryKey), 'gateways'],
    queryFn: async () => {
      const response = await api.get(`/smswiser/countries/${countryKey}/gateways`);
      return unwrapApiData<Gateway[]>(response.data);
    },
    enabled: !!countryKey,
    staleTime: 3600000,
  });
}

export function useLeadsListsByCountry(countryKey: string) {
  return useQuery({
    queryKey: [...smswiserKeys.country(countryKey), 'leads-lists'],
    queryFn: async () => {
      const response = await api.get(`/smswiser/countries/${countryKey}/leads-lists`);
      return unwrapApiData<LeadsList[]>(response.data);
    },
    enabled: !!countryKey,
    staleTime: 300000, // 5 minutes (leads count changes)
  });
}

/** Params to align tag Voluum request with lists-with-stats (same tz, currency, from, to). */
export interface ListPerformanceTagsParams {
  from: string;
  to: string;
  tz?: string;
  currency?: string;
}

/** Stable cache key segment for dashboard params. Ensures same params = same cache. */
export function dashboardParamsKey(p: ListPerformanceTagsParams | null | undefined): string {
  if (!p?.from || !p?.to) return 'default';
  return `${p.from}|${p.to}|${p.tz ?? ''}|${p.currency ?? ''}`;
}

/** Aligns with countries.controller non-phone *-performance-tags: @Permissions("drops.view"). */
export function useDropsViewPerfTagsEnabled(countryKey: string, enabled: boolean): boolean {
  const { hasPermission } = useAuth();
  return !!countryKey && enabled && hasPermission('drops.view');
}

/** Aligns with phone-performance-tags: @Permissions("stats.phone", "drops.view"). */
function usePhonePerfTagsEnabled(countryKey: string, enabled: boolean): boolean {
  const { hasPermission } = useAuth();
  return (
    !!countryKey &&
    enabled &&
    (hasPermission('stats.phone') || hasPermission('drops.view'))
  );
}

/** Shared fetcher so campaign-scoped queries build the identical request everywhere. */
export async function fetchListPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
): Promise<ListPerformanceTagsResponse> {
  const params: Record<string, string> = {};
  if (wiserIds != null && wiserIds.length > 0) {
    params.wiserIds = wiserIds.join(',');
  }
  if (dashboardParams?.from) params.from = dashboardParams.from;
  if (dashboardParams?.to) params.to = dashboardParams.to;
  if (dashboardParams?.tz) params.tz = dashboardParams.tz;
  if (dashboardParams?.currency) params.currency = dashboardParams.currency;
  const response = await api.get(
    `/smswiser/countries/${encodeURIComponent(countryKey)}/list-performance-tags`,
    { params },
  );
  return unwrapApiData<ListPerformanceTagsResponse>(response.data);
}

/** Performance tags per list. Pass params when on lists stats so Voluum request matches lists-with-stats. */
export function useListPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  /** Same as lists-with-stats: from, to, tz, currency — ensures identical Voluum request when preset matches. */
  dashboardParams?: ListPerformanceTagsParams | null,
  /** When false, skips the fetch (e.g. when parent provides perfTagsQuery). Avoids duplicate ~4s request. */
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'list-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: () => fetchListPerformanceTags(countryKey, wiserIds, dashboardParams),
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000, // 2 hours — refresh only on Cmd+R or when expired; persisted to localStorage
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export interface TextPerformanceTagsResponse {
  tagsByTextId: Record<string, PerformanceTag[]>;
  tagDetailsByTextId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface GatewayPerformanceTagsResponse {
  tagsByGatewayId: Record<string, PerformanceTag[]>;
  tagDetailsByGatewayId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface PathPerformanceTagsResponse {
  tagsByPathId: Record<string, PerformanceTag[]>;
  tagDetailsByPathId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface LanderPerformanceTagsResponse {
  tagsByLanderId: Record<string, PerformanceTag[]>;
  tagDetailsByLanderId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface OfferPerformanceTagsResponse {
  tagsByOfferId: Record<string, PerformanceTag[]>;
  tagDetailsByOfferId?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export interface PhonePerformanceTagsResponse {
  tagsByPhoneKey: Record<string, PerformanceTag[]>;
  tagDetailsByPhoneKey?: Record<string, Array<{ tag: string; dateRangePreset: string }>>;
  tagColors?: Record<string, string>;
  currency?: string;
  from: string;
  to: string;
}

export function useTextPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'text-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/text-performance-tags`,
        { params },
      );
      return unwrapApiData<TextPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function useGatewayPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'gateway-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/gateway-performance-tags`,
        { params },
      );
      return unwrapApiData<GatewayPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function usePathPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'path-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/path-performance-tags`,
        { params },
      );
      return unwrapApiData<PathPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function useLanderPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'lander-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/lander-performance-tags`,
        { params },
      );
      return unwrapApiData<LanderPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function useOfferPerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = useDropsViewPerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'offer-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/offer-performance-tags`,
        { params },
      );
      return unwrapApiData<OfferPerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

export function usePhonePerformanceTags(
  countryKey: string,
  wiserIds?: number[],
  dashboardParams?: ListPerformanceTagsParams | null,
  enabled = true,
) {
  const queryEnabled = usePhonePerfTagsEnabled(countryKey, enabled);
  return useQuery({
    queryKey: [
      ...smswiserKeys.country(countryKey),
      'phone-performance-tags',
      wiserIds ?? [],
      dashboardParamsKey(dashboardParams),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (wiserIds != null && wiserIds.length > 0) {
        params.wiserIds = wiserIds.join(',');
      }
      if (dashboardParams?.from) params.from = dashboardParams.from;
      if (dashboardParams?.to) params.to = dashboardParams.to;
      if (dashboardParams?.tz) params.tz = dashboardParams.tz;
      if (dashboardParams?.currency) params.currency = dashboardParams.currency;
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/phone-performance-tags`,
        { params },
      );
      return unwrapApiData<PhonePerformanceTagsResponse>(response.data);
    },
    enabled: queryEnabled,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
}

/** Map of leadsListWiserId -> amount used for the work date (from prepared drops). */
export type LeadsUsageMap = Record<string, number>;

export function useLeadsUsage(countryKey: string, workDate: string) {
  return useQuery({
    queryKey: [...smswiserKeys.country(countryKey), 'leads-usage', workDate],
    queryFn: async () => {
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/leads-usage`,
        { params: { workDate } },
      );
      return unwrapApiData<LeadsUsageMap>(response.data);
    },
    enabled: !!countryKey && !!workDate,
    staleTime: 60000, // 1 min (usage changes when drafts are created/sent)
  });
}

export function useCreateDrop() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDropDto) => {
      const response = await api.post('/smswiser/drops', data);
      return unwrapApiData<{ drop: Drop }>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smswiserKeys.drops() });
    },
  });
}

/** Derive a simple status label from the SMSWiser drop status object. */
function smswiserDropStatusLabel(d: SmswiserDrop): string {
  const s = d.status;
  if (s?.isDraft) return 'draft';
  if (s?.Stopped) return 'stopped';
  if (d.finishedAt) return 'completed';
  if (s?.Paused) return 'paused';
  if (s?.startedAt) return 'active';
  if (d.executeAfter) return 'scheduled';
  return 'pending';
}

function toCampaignDrop(d: SmswiserDrop): Drop {
  return {
    id: String(d.id),
    uniqueId: d.uniqueId,
    title: d.title,
    status: smswiserDropStatusLabel(d),
    country: d.country,
    cap: d.cap,
    createdAt: d.executeAfter ?? d.finishedAt ?? undefined,
  };
}

/**
 * All drops, mapped to the lightweight Drop shape (used by the campaign Drops tab,
 * filtered client-side by country). Gated via `enabled` so it only runs where needed
 * — the SMSWiser list endpoint is `/drops/all` (DataTables-style), not `/drops`.
 */
export function useDrops(enabled = true) {
  return useQuery({
    queryKey: smswiserKeys.drops(),
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: SmswiserDropsAllResponse }>(
        '/smswiser/drops/all',
        { params: { length: 2000 } },
      );
      return (data.data?.data ?? []).map(toCampaignDrop);
    },
    enabled,
    staleTime: 30000, // 30 seconds
  });
}

export interface SenderTemplate {
  id: string;
  name: string;
  senderNames: string[];
}

export const gatewaySenderTemplateKeys = {
  all: ['smswiser', 'gateway-sender-templates'] as const,
  list: (countryKey: string, routeId: number) =>
    [...gatewaySenderTemplateKeys.all, countryKey, routeId] as const,
};

export function useGatewaySenderTemplates(countryKey: string, routeId: number) {
  return useQuery({
    queryKey: gatewaySenderTemplateKeys.list(countryKey, routeId),
    queryFn: async () => {
      const response = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/gateways/${routeId}/sender-templates`,
      );
      return unwrapApiData<SenderTemplate[]>(response.data);
    },
    enabled: !!countryKey && Number.isFinite(routeId) && routeId > 0,
    staleTime: 60 * 1000,
    // Dedupe: avoid refetch on mount when data is fresh (reduces Strict Mode double-fetch)
    refetchOnMount: false,
    // Share cache across components - prevents duplicate requests when multiple forms use same gateway
    refetchOnWindowFocus: false,
  });
}

export function useCreateGatewaySenderTemplate(
  countryKey: string,
  routeId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { name?: string; senderNames: string[] }) => {
      const response = await api.post(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/gateways/${routeId}/sender-templates`,
        params,
      );
      return unwrapApiData<SenderTemplate>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: gatewaySenderTemplateKeys.list(countryKey, routeId),
      });
    },
  });
}

export function useUpdateGatewaySenderTemplate(
  countryKey: string,
  routeId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      name?: string;
      senderNames?: string[];
    }) => {
      const { id, ...body } = params;
      const response = await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/gateways/${routeId}/sender-templates/${id}`,
        body,
      );
      return unwrapApiData<SenderTemplate>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: gatewaySenderTemplateKeys.list(countryKey, routeId),
      });
    },
  });
}

export function useDeleteGatewaySenderTemplate(
  countryKey: string,
  routeId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/gateways/${routeId}/sender-templates/${id}`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: gatewaySenderTemplateKeys.list(countryKey, routeId),
      });
    },
  });
}
